import dayjs, { isValidDate } from '../../../utils/dayjs';
import {
  Box,
  Form,
  Card,
  Divider,
  FormLayout,
  InlineGrid,
  Select,
  Text,
  TextField,
  BlockStack,
  useBreakpoints,
  ContextualSaveBar,
} from '@shopify/polaris';

import { useEffect, useState } from 'react';

import helpers from 'helpers';
import { lengthLessThan, lengthMoreThan, notEmptyString, useField, useForm } from '@shopify/react-form';

import DateTimeInput from 'components/dateTimeInput';
import { useGetCurrentUserData, useUpdateProfile } from 'queries/user.query';
import { useNotification } from 'context/NotificationContext';
import __ from 'languages/index';

/**
 *   Create upload Modal for Notification
 */
export default function EditMyInformation() {
  // const { user: entity } = useAuth();
  const { addNotification } = useNotification();

  const { smUp } = useBreakpoints();

  const { mutateAsync: updateProfile, isSuccess: updateProfileSuccess } = useUpdateProfile();
  const { data: entity } = useGetCurrentUserData();

  useEffect(() => {
    if (updateProfileSuccess) {
      addNotification('info', __('updated_successfully'));
    }
  }, [updateProfileSuccess]);

  const useFields = {
    user_login: useField<string>({
      value: entity?.user_login || '',
      validates: [
        (inputValue) => {
          if (inputValue && !/^[a-zA-Z]/.test(inputValue)) {
            return 'Tên đăng nhập phải bắt đầu bằng một chữ cái (A–Z hoặc a–z).';
          }
        },
        lengthLessThan(60, 'Không được dài hơn 60 ký tự.'),
        lengthMoreThan(6, 'Không được ngắn hơn 6 ký tự.'),
        (inputValue) => {
          if (inputValue.length > 1) {
            if (helpers.isUTF8(inputValue)) {
              return 'Không được dùng mã Unicode trong trường này!';
            }
            if (/^[a-zA-Z0-9_.]+$/.exec(inputValue) === null) {
              return 'Chỉ sử dụng chữ cái, số và dấu chấm';
            }
          }
        },
      ],
    }),

    referrer_code: useField<string>({
      value: entity.referrer_code || '',
      validates: [
        (inputValue) => {
          if (inputValue.length > 0) {
            if (helpers.isUTF8(inputValue)) {
              return 'Không được dùng mã Unicode trong trường này!';
            }
            if (/^[a-zA-Z0-9_.]+$/.exec(inputValue) === null) {
              return 'Chỉ sử dụng chữ cái, số và dấu gạch dưới.';
            }
            if (inputValue.length > 12) {
              return 'Không được dài hơn 12 ký tự.';
            }
            if (inputValue.length < 6) {
              return 'Quá ngắn, yêu cầu 6 ký tự trở lên.';
            }
          }
        },
      ],
    }),

    user_email: useField<string>({
      value: entity?.user_email ?? '',
      validates: [
        (inputValue) => {
          if (inputValue && !helpers.isEmail(inputValue)) {
            return 'Your email is not valid!';
          }
        },
      ],
    }),

    display_name: useField<string>({
      value: entity?.display_name || '',
      validates: [lengthLessThan(60, 'Tên của bạn quá dài!'), lengthMoreThan(2, 'Tên của bạn quá ngắn!')],
    }),

    bio: useField<string>({ value: entity?.bio || '', validates: [lengthLessThan(250, 'Mô tả quá dài!')] }),

    user_address: useField<string>({ value: entity?.user_address || '', validates: [lengthLessThan(250, 'Mô tả quá dài!')] }),

    user_birthday: useField<string>({
      value: entity?.user_birthday ? dayjs(new Date(entity?.user_birthday)).format('YYYY-MM-DD') : '',
      validates: [
        (inputVal) => {
          if (inputVal) {
            let checkDate = isValidDate(inputVal, 'YYYY-MM-DD');
            if (!checkDate) return __('check_this_field_again');
          }
        },
      ],
    }),

    user_phonenumber: useField<string>({
      value: entity?.user_phonenumber || '',
      validates: [
        (inputValue) => {
          if (inputValue) {
            if (!new RegExp('^[0-9]+$', 'g').test(inputValue)) {
              return 'Định dạng số điện thoại không hợp lệ. Chỉ dùng số và dấu cách!';
            }
          }
        },
      ],
    }),

    user_title: useField<string>({ value: entity?.user_title || '', validates: [] }),
  };

  const {
    fields,
    submit,
    submitting,
    dirty,
    reset: resetForm,
  } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await updateProfile({
          user_id: entity?.user_id,
          referrer_code: values.referrer_code || undefined,
          user_login: values.user_login || undefined,
          display_name: values.display_name,
          bio: values.bio,
          user_title: values.user_title,
          user_address: values.user_address,
          user_phonenumber: values.user_phonenumber,
          user_birthday: values.user_birthday || null,
        });
        resetForm();
      } catch (e: any) {
        console.error(`Submit error`, e);
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success', errors: [] };
    },
  });

  return (
    <>
      {dirty && (
        <ContextualSaveBar
          message="Thay đổi chưa được lưu"
          saveAction={{ content: 'Cập nhật', onAction: () => submit(), loading: submitting }}
          discardAction={{ content: 'Xóa hết', onAction: () => resetForm() }}
        />
      )}

      <Form onSubmit={submit}>
        <BlockStack gap={{ xs: '800', sm: '400' }}>
          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Thông tin chung
                </Text>
                <Text as="p" variant="bodyMd">
                  Càng chi tiết càng tốt.
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <BlockStack gap="400">
                <FormLayout>
                  <TextField
                    autoFocus
                    autoComplete="off"
                    label="user_login"
                    helpText={<p>user_login không được phép trùng lặp với bất kỳ một tài khoản nào!</p>}
                    {...fields.user_login}
                  />
                  <TextField
                    autoComplete="off"
                    label="Mã giới thiệu"
                    maxLength={12}
                    helpText={<p>Không nên đổi mã nhiều lần, dẫn tới mã cũ bị vô hiệu. Mã này phải độc nhất.</p>}
                    {...fields.referrer_code}
                  />

                  <TextField
                    label="Tên hiển thị"
                    autoComplete="off"
                    error={fields.display_name.error}
                    value={fields.display_name.value}
                    onChange={(e) => fields.display_name.onChange(helpers.capitalizeWords(e))}
                    connectedLeft={
                      <Select
                        value={useFields.user_title.value || undefined}
                        options={[
                          { value: '', label: 'Không set' },
                          { value: 'mr', label: 'Anh' },
                          { value: 'miss', label: 'Cô' },
                          { value: 'mrs', label: 'Chị' },

                          { value: 'madam', label: 'Quý bà' },
                          { value: 'sir', label: 'Quý ông' },
                        ]}
                        onChange={(e) => useFields.user_title.onChange(e)}
                        label={''}
                      />
                    }
                  />

                  <DateTimeInput
                    onDateChange={(dateObjectOrnull) => {
                      if (!dateObjectOrnull) {
                        fields.user_birthday.onChange(``);
                      } else {
                        let { day, month, year } = dateObjectOrnull;
                        fields.user_birthday.onChange(`${year}-${month}-${day}`);
                      }
                    }}
                    label={'Sinh nhật'}
                    initialDate={fields.user_birthday.value}
                    error={fields.user_birthday.error}
                  />

                  <TextField
                    maxLength={250}
                    max={250}
                    showCharacterCount={true}
                    autoComplete="off"
                    label="Giới thiệu ngắn"
                    {...fields.bio}
                    multiline={3}
                  />
                </FormLayout>
              </BlockStack>
            </Card>
          </InlineGrid>

          {smUp ? <Divider /> : null}
          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Thông tin liên hệ
                </Text>
                <Text as="p" variant="bodyMd">
                  Càng đầy đủ thông tin càng tốt
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <BlockStack gap="400">
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label="Số điện thoại"
                      autoComplete="off"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...fields.user_phonenumber}
                    />
                    <TextField label="Email" autoComplete="off" {...fields.user_email} />
                  </FormLayout.Group>

                  <TextField label="Địa chỉ" autoComplete="off" {...fields.user_address} />
                </FormLayout>
              </BlockStack>
            </Card>
          </InlineGrid>
        </BlockStack>

        <br />
        <br />
      </Form>
    </>
  );
}

